import { A } from "@solidjs/router";
import {
  type Component,
  createMemo,
  createSignal,
  createUniqueId,
  onMount,
  Show,
  untrack,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Switch,
  SwitchControl,
  SwitchLabel,
  SwitchThumb,
} from "@/components/ui/switch";
import { TextArea } from "@/components/ui/textarea";
import { TextFieldLabel, TextFieldRoot } from "@/components/ui/textfield";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { useTalentPool } from "@/domains/company/talent-pool/context/talent-pool-provider";

const UPGRADE_HREF = "/profile/company/upgrade";

export type SaveFieldsProps = {
  freelancerProfileId: string;
  /**
   * Called once on mount with a `commit` function that flushes a dirty note via
   * `updateSaved`. The enclosing surface (popover / sheet panel) invokes it on
   * every dismissal path so an unsaved note is persisted (D8). The commit is a
   * no-op when the note is untouched or the entry is read-only, so an untouched
   * dismissal issues zero `updateSaved` calls.
   */
  registerCommit?: (commit: () => void) => void;
};

/**
 * `SaveFields` — presentation-neutral note + visibility editor shared by the
 * card `SavePopover` and (later) the sheet save panel. It reads the saved entry
 * and plan capabilities from `useTalentPool()` and owns:
 *
 * - a **note** draft with a local dirty flag; the draft is persisted on the
 *   surface's dismissal (via `registerCommit`) — never on every keystroke;
 * - a **visibility** toggle (only when `capabilities.can_share`) that persists
 *   immediately on change (Req 5), with the helper copy swapping off ↔ on;
 * - a **read-only** rendering for colleague-shared entries (`saved_by_me:false`,
 *   D6): the note is shown as text, with no editable controls.
 */
export const SaveFields: Component<SaveFieldsProps> = (props) => {
  const { t } = useI18n();
  const talentPool = useTalentPool();
  const [companyState] = useCompanyStore();

  const noteId = createUniqueId();

  const entry = createMemo(() => talentPool.entry(props.freelancerProfileId));
  const isReadOnly = createMemo(() => entry()?.saved_by_me === false);
  const workspace = createMemo(() => companyState.selectedCompany?.name ?? "");

  const [noteDraft, setNoteDraft] = createSignal(
    untrack(() => entry()?.notes ?? ""),
  );
  const [noteDirty, setNoteDirty] = createSignal(false);
  const [sharedOn, setSharedOn] = createSignal(
    untrack(() => entry()?.visibility === "shared"),
  );

  // Stable closure: reads the current signals at call time, so a single
  // reference handed to the parent always flushes the latest draft. Gated by
  // the local dirty flag (belt-and-suspenders with the provider's blind-PATCH
  // guard) so an untouched dismissal calls `updateSaved` zero times.
  const commit = () => {
    if (isReadOnly()) return;
    if (!noteDirty()) return;
    setNoteDirty(false);
    void talentPool.updateSaved(props.freelancerProfileId, {
      notes: noteDraft(),
    });
  };

  onMount(() => props.registerCommit?.(commit));

  const handleNoteInput = (value: string) => {
    setNoteDraft(value);
    setNoteDirty(true);
  };

  const handleToggle = (checked: boolean) => {
    setSharedOn(checked);
    void talentPool.updateSaved(props.freelancerProfileId, {
      visibility: checked ? "shared" : "private",
    });
  };

  // Persist a dirty note before the router navigates to the upgrade page (D8:
  // persist THEN navigate). `<A>` runs this handler before the navigation.
  const handleUpgradeClick = () => commit();

  return (
    <div class="flex flex-col gap-4">
      <Show
        when={!isReadOnly()}
        fallback={
          <div class="flex flex-col gap-1">
            <p class="text-sm font-medium">
              {t("company.talent_pool.popover.note.label")}
            </p>
            <p class="text-sm text-muted-foreground whitespace-pre-wrap">
              {entry()?.notes ?? ""}
            </p>
          </div>
        }
      >
        <TextFieldRoot>
          <TextFieldLabel for={noteId}>
            {t("company.talent_pool.popover.note.label")}
          </TextFieldLabel>
          <TextArea
            id={noteId}
            value={noteDraft()}
            onInput={(e) => handleNoteInput(e.currentTarget.value)}
            placeholder={t("company.talent_pool.popover.note.placeholder")}
            class="min-h-[80px]"
          />
        </TextFieldRoot>
      </Show>

      <Show when={!isReadOnly()}>
        <div class="flex flex-col gap-2">
          <p class="text-sm font-medium">
            {t("company.talent_pool.popover.visibility.label")}
          </p>

          <Show
            when={talentPool.capabilities.can_share}
            fallback={
              <div class="flex flex-col gap-2.5">
                {/* Disabled "team visibility" row — single-seat plans force
                    everything private, so the control is shown inert. */}
                <div class="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                  <span class="flex items-center gap-2 text-sm font-medium text-gray-400">
                    <i class="ph ph-users-three" aria-hidden="true" />
                    {t("company.talent_pool.popover.visibility.toggle_label")}
                  </span>
                  <span class="flex items-center gap-2">
                    <span class="relative inline-block h-6 w-10 rounded-full bg-gray-200">
                      <span class="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm" />
                    </span>
                    <i
                      class="ph ph-lock-key text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </div>
                {/* Amber upgrade callout — sharing is a Team-plan capability. */}
                <div class="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <i
                    class="ph ph-lock-key mt-0.5 shrink-0 text-base text-amber-700"
                    aria-hidden="true"
                  />
                  <p class="text-xs leading-relaxed text-amber-800">
                    {t("company.talent_pool.popover.visibility.team_notice")}.{" "}
                    <Button
                      as={A}
                      href={UPGRADE_HREF}
                      onClick={handleUpgradeClick}
                      variant="link"
                      class="h-auto p-0! text-xs font-semibold"
                    >
                      {t(
                        "company.talent_pool.popover.visibility.team_notice_link",
                      )}
                    </Button>
                  </p>
                </div>
              </div>
            }
          >
            <div class="flex flex-col gap-1">
              <Switch
                checked={sharedOn()}
                onChange={handleToggle}
                class="flex items-center justify-between gap-2"
              >
                <SwitchLabel class="text-sm">
                  {t("company.talent_pool.popover.visibility.toggle_label")}
                </SwitchLabel>
                <SwitchControl>
                  <SwitchThumb />
                </SwitchControl>
              </Switch>
              <p class="text-sm text-muted-foreground">
                <Show
                  when={sharedOn()}
                  fallback={t(
                    "company.talent_pool.popover.visibility.helper_off",
                  )}
                >
                  {t("company.talent_pool.popover.visibility.helper_on", {
                    workspace: workspace(),
                  })}
                </Show>
              </p>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
};
